import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Plus, Check, Trash2, Edit2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

export default function BarnChoresPage() {
  const { authHeaders, API } = useAuth();
  const [chores, setChores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editChore, setEditChore] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', frequency: '', assigned_to: '', due_date: '', completed: false, notes: '' });

  const fetchChores = async () => {
    try {
      const res = await axios.get(`${API}/barn-chores`, authHeaders());
      setChores(res.data);
    } catch (err) {
      toast.error('Failed to load chores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChores(); }, []); // eslint-disable-line

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editChore) {
        await axios.put(`${API}/barn-chores/${editChore.id}`, form, authHeaders());
        toast.success('Chore updated!');
      } else {
        await axios.post(`${API}/barn-chores`, form, authHeaders());
        toast.success('Chore added!');
      }
      setShowDialog(false);
      setEditChore(null);
      setForm({ title: '', description: '', frequency: '', assigned_to: '', due_date: '', completed: false, notes: '' });
      fetchChores();
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const toggleComplete = async (chore) => {
    try {
      await axios.put(`${API}/barn-chores/${chore.id}`, { ...chore, completed: !chore.completed }, authHeaders());
      fetchChores();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (choreId) => {
    try {
      await axios.delete(`${API}/barn-chores/${choreId}`, authHeaders());
      toast.success('Chore deleted');
      fetchChores();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (chore) => {
    setEditChore(chore);
    setForm({
      title: chore.title, description: chore.description || '', frequency: chore.frequency || '',
      assigned_to: chore.assigned_to || '', due_date: chore.due_date || '', completed: chore.completed, notes: chore.notes || ''
    });
    setShowDialog(true);
  };

  const openAdd = () => {
    setEditChore(null);
    setForm({ title: '', description: '', frequency: '', assigned_to: '', due_date: '', completed: false, notes: '' });
    setShowDialog(true);
  };

  const pending = chores.filter(c => !c.completed);
  const completed = chores.filter(c => c.completed);

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 animate-fade-in">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="barn-chores-title">
              Barn Chore Planner
            </h1>
            <p className="text-[#4B6B58] mt-1">Keep your barn running smoothly with organized tasks.</p>
          </div>
          <button
            onClick={openAdd}
            data-testid="add-chore-button"
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]"
          >
            <Plus size={18} /> Add Chore
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-[2rem] h-20 animate-pulse"></div>)}
          </div>
        ) : chores.length === 0 ? (
          <div className="bg-white rounded-[2rem] p-12 text-center shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid="no-chores-message">
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-2">No Chores Yet</h3>
            <p className="text-[#4B6B58] mb-6">Add your first barn chore to get organized.</p>
            <button onClick={openAdd} data-testid="add-first-chore-button" className="px-8 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]">
              Add First Chore
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Pending */}
            {pending.length > 0 && (
              <div className="animate-fade-in">
                <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4">Pending ({pending.length})</h2>
                <div className="space-y-3">
                  {pending.map(chore => (
                    <div key={chore.id} className="bg-white rounded-[1.5rem] p-5 flex items-start gap-4 card-hover shadow-[0_4px_12px_rgb(27,67,50,0.04)]" data-testid={`chore-${chore.id}`}>
                      <button onClick={() => toggleComplete(chore)} data-testid={`toggle-chore-${chore.id}`} className="mt-0.5 w-6 h-6 rounded-full border-2 border-[#D1E0D5] flex-shrink-0 hover:border-[#2D6A4F] btn-lift"></button>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#122A1F]">{chore.title}</h3>
                        {chore.description && <p className="text-sm text-[#4B6B58] mt-1">{chore.description}</p>}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {chore.frequency && <span className="px-3 py-1 rounded-full bg-[#E9EFE8] text-xs text-[#4B6B58]">{chore.frequency}</span>}
                          {chore.assigned_to && <span className="px-3 py-1 rounded-full bg-[#F9F7F3] text-xs text-[#4B6B58]">{chore.assigned_to}</span>}
                          {chore.due_date && <span className="px-3 py-1 rounded-full bg-[#E9EFE8] text-xs text-[#4B6B58]">{chore.due_date}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button onClick={() => openEdit(chore)} data-testid={`edit-chore-${chore.id}`} className="p-2 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58]"><Edit2 size={14} /></button>
                        <button onClick={() => handleDelete(chore.id)} data-testid={`delete-chore-${chore.id}`} className="p-2 rounded-full hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Completed */}
            {completed.length > 0 && (
              <div className="animate-fade-in-delay-1">
                <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#4B6B58] mb-4">Completed ({completed.length})</h2>
                <div className="space-y-3">
                  {completed.map(chore => (
                    <div key={chore.id} className="bg-[#F9F7F3] rounded-[1.5rem] p-5 flex items-start gap-4 opacity-75" data-testid={`chore-completed-${chore.id}`}>
                      <button onClick={() => toggleComplete(chore)} data-testid={`toggle-complete-${chore.id}`} className="mt-0.5 w-6 h-6 rounded-full bg-[#2D6A4F] flex-shrink-0 flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </button>
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#4B6B58] line-through">{chore.title}</h3>
                      </div>
                      <button onClick={() => handleDelete(chore.id)} className="p-2 rounded-full hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-md" data-testid="chore-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">
              {editChore ? 'Edit Chore' : 'Add New Chore'}
            </DialogTitle>
            <DialogDescription className="text-[#4B6B58]">
              {editChore ? 'Update this barn chore.' : 'Add a new barn chore.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-1">Title *</label>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required data-testid="chore-title-input" className="stable-input w-full" placeholder="e.g., Clean stalls" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="chore-description-input" className="stable-input w-full" rows={2} placeholder="Details..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#122A1F] mb-1">Frequency</label>
                <input value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})} data-testid="chore-frequency-input" className="stable-input w-full" placeholder="Daily, Weekly..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#122A1F] mb-1">Assigned To</label>
                <input value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})} data-testid="chore-assigned-input" className="stable-input w-full" placeholder="Name" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-1">Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} data-testid="chore-due-date-input" className="stable-input w-full" />
            </div>
            <button type="submit" data-testid="chore-save-button" className="w-full py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]">
              {editChore ? 'Update Chore' : 'Add Chore'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
